import { closeSidePanel, updateSidePanel, useActiveRightStackJid } from '@/app/store';
import { hidePopup, showPopup } from '@/components/PopupContainer/popupService';
import { useGroupParticipants, useMyRoleInGroup } from '@/features/chats/model/groupParticipants';
import { isGroupChat } from '@/features/chats/utils';
import { getUserNickName, useUserDetails } from '@/features/users/model';
import { handleOpenPhotoViewer } from '@/features/users/utils';
import { useLanguage } from '@/localization/useLanguage';
import { getLocalUserId, getUserIdFromJid, logger } from '@/shared/utils';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useChatRequest } from '@/features/chats/viewmodels/useChatRequest';
import AddParticipants from '../../group-chat/ui/AddParticipants';
import GroupParticipants from '../ui/components/GroupParticipants';
import {
   chatMuteAction,
   clearChatAction,
   deleteChatAction,
   openMediaGallery,
   reportChatAction,
} from '../utils/actionUtils';
import { handleGroupMemberAction } from '../utils/utils';

/**
 * ViewModel for ChatInfo sub-feature.
 * Centralizes all logic for Profile/Group Info, Muting, Member Actions, and Group Management.
 */
export const useChatInfo = propsJid => {
   const { t } = useLanguage();
   const dispatch = useDispatch();
   const rightStackJid = useActiveRightStackJid();
   const sidePanelTargetJid = useSelector(state => state.navigationData.sidePanel.data?.targetJid);
   const activeChatJid = sidePanelTargetJid || propsJid || rightStackJid;
   const userId = getUserIdFromJid(activeChatJid);
   const isGroup = useMemo(() => activeChatJid && isGroupChat(activeChatJid), [activeChatJid]);

   const user = useSelector(state => state.userInfoData?.users?.[userId]);
   const { nickName, image, status, isMuted } = user || {};
   const { isBlockedByMe, isDeleted } = useUserDetails(userId, ['isBlockedByMe', 'isDeleted']);
   const { isFeatureGated, isReceiver } = useChatRequest(activeChatJid);

   const [searchText, setSearchText] = useState('');

   // Group specific
   const { participants, totalParticipantsCount } = useGroupParticipants(userId, searchText);

   const myRole = useMyRoleInGroup(userId);
   const amIAdmin = myRole === 'o';
   const amIParticipant = !!myRole;
   const existingMemberIds = useMemo(() => participants?.map(p => p.userId), [participants]);

   const handleClose = useCallback(() => {
      closeSidePanel();
   }, []);

   const handleMuteToggle = useCallback(() => {
      chatMuteAction(activeChatJid, !isMuted);
   }, [activeChatJid, isMuted]);

   const handleAction = useCallback(
      (key, onClose) => {
         switch (key) {
            case 'clear':
               clearChatAction(activeChatJid, onClose);
               break;
            case 'report':
               reportChatAction(activeChatJid, onClose);
               break;
            case 'exit':
               handleGroupMemberAction({ groupId: userId, userId: getLocalUserId(), action: 'exitGroup' });
               onClose?.();
               break;
            case 'delete':
               deleteChatAction(activeChatJid, onClose);
               break;
            default:
               break;
         }
      },
      [activeChatJid, userId],
   );

   const handleOpenAddParticipant = useCallback(() => {
      const modalId = 'addParticipantModal';
      const title = t('group.ADD_PARTICIPANTS');
      showPopup({
         id: modalId,
         visible: true,
         title,
         body: (
            <AddParticipants
               existingGroupId={userId}
               existingMemberIds={existingMemberIds}
               onClose={() => hidePopup(modalId)}
            />
         ),
         onRequestClose: () => hidePopup(modalId),
      });
   }, [userId, existingMemberIds, t]);

   const handleExpandGroupParticipants = useCallback(() => {
      const modalId = 'groupMembersModal';
      const title = t('group.GROUP_PARTICIPANTS');
      showPopup({
         visible: true,
         id: modalId,
         title,
         body: <GroupParticipants groupId={userId} groupName={getUserNickName(userId)} />,
         onRequestClose: () => hidePopup(modalId),
      });
   }, [userId, t]);

   const handleCall = useCallback(
      callType => {
         // In a real app, this would dispatch calling actions
         logger.log(`Starting ${callType} call with ${activeChatJid}`);
      },
      [activeChatJid],
   );

   const handleOpenMediaGallery = useCallback(() => {
      openMediaGallery();
   }, []);

   const handleEditIcon = useCallback(() => {
      dispatch(updateSidePanel({ id: 'group-profile-edit', isOpen: true }));
   }, [dispatch]);

   const handlePhotoClick = useCallback(() => {
      if (!isBlockedByMe && image) {
         handleOpenPhotoViewer({ imageUri: image, userId });
      }
   }, [isBlockedByMe, image, userId]);

   const isPhotoClickable = !!(!isBlockedByMe && image);

   return {
      // Data
      activeChatJid,
      userId,
      isGroup,
      nickName,
      image,
      status: status || 'Available',
      isMuted,
      isBlockedByMe,
      isDeleted,
      amIAdmin,
      amIParticipant,
      participants,
      totalParticipantsCount,
      searchText,
      existingMemberIds,
      // Handlers
      setSearchText,
      handleClose,
      handleMuteToggle,
      handleAction,
      handleOpenAddParticipant,
      handleCall,
      handleExpandGroupParticipants,
      handleOpenMediaGallery,
      handleEditIcon,
      handlePhotoClick,
      isPhotoClickable,
      isFeatureGated,
      isReceiver,
   };
};
